import { secureGet, securePut, securePost } from '../utils/HttpUtils';

const apiUrls = {
  approvedAmounts: '/v1/private/applications/approved-amounts',
  approvedLines: '/v1/private/approved-lines',
  guaranteeDetail: '/v1/private/guarantees',
  productLineDetail: '/v1/private/guarantees/approved-line',
  drawerByCuit: '/v1/private/guarantees/drawers',
  overdueFilters: '/v1/private/guarantees/overdue/filters',
  overdue: '/v1/private/guarantees/overdue',
  guaranteeStatus: '/v1/private/guarantees/status',
  ongoing: '/v1/private/guarantees',
  guaranteeGeneralLoans: '/v1/private/guarantees/general/loans',
  guaranteeGeneralChecks: '/v1/private/guarantees/general/checks',
  guaranteeGeneralPromissoryNotes: '/v1/private/guarantees/general/promissory-notes',
  guaranteeGeneralCommercialEndorsements: '/v1/private/guarantees/general/commercial-endorsements',
  commissionTypes: '/v1/private/guarantees/commission-types',
  commissions: '/v1/private/guarantees/commissions',
  purposeTypes: '/v1/private/guarantees/purpose-types',
  riskMetrics: '/v1/private/guarantees/risk-metrics',
  riskMetricsProtectors: '/v1/private/guarantees/risk-metrics/protectors',
  protectorDetail: '/v1/private/guarantees/protectors',
};

export const getApprovedAmounts = async (
  page: number = 0,
  size: number = 10,
  searchText: string = '',
  filters?: Record<string, string>
) => {
  try {
    let url = `${apiUrls.approvedAmounts}?page=${page}&size=${size}`;

    if (searchText && searchText.trim() !== '') {
      let processedSearchText = searchText.trim();

      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedSearchText)) {
        processedSearchText = processedSearchText.replace(/-/g, '');
      }

      url += `&filterText=${encodeURIComponent(processedSearchText)}`;
    }

    // Agregar filtros adicionales si existen
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching approved amounts:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getApprovedLines = async (
  filterText: string = '',
  page: number = 0,
  size: number = 10
) => {
  try {
    let url = `${apiUrls.approvedLines}?page=${page}&size=${size}`;

    if (filterText && filterText.trim() !== '') {
      let processedFilterText = filterText.trim();

      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedFilterText)) {
        processedFilterText = processedFilterText.replace(/-/g, '');
      }

      url += `&filterText=${encodeURIComponent(processedFilterText)}`;
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching approved lines:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeDetail = async (cuit: string) => {
  try {
    const url = `${apiUrls.guaranteeDetail}/${cuit}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getProductLineDetail = async (productLineId: string) => {
  try {
    const url = `${apiUrls.productLineDetail}/${productLineId}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching product line detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getDrawerByCuit = async (cuit: string) => {
  try {
    const url = `${apiUrls.drawerByCuit}/${cuit}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching drawer by CUIT:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getOverdueFilters = async () => {
  try {
    const url = apiUrls.overdueFilters;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching overdue filters:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getOverdue = async (
  page: number = 0,
  size: number = 10,
  date?: string,
  creditor?: string,
  type?: string,
  filterText?: string
) => {
  try {
    let url = `${apiUrls.overdue}?page=${page}&size=${size}`;

    if (date) {
      url += `&dueDate=${encodeURIComponent(date)}`;
    }
    if (creditor) {
      url += `&creditorId=${encodeURIComponent(creditor)}`;
    }
    if (type) {
      url += `&type=${encodeURIComponent(type)}`;
    }
    if (filterText && filterText.trim() !== '') {
      url += `&filterText=${encodeURIComponent(filterText.trim())}`;
    }

    return secureGet(url);
  } catch (error) {
    console.error('Error fetching overdue:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateGuaranteeStatus = async (
  id_global: number,
  installmentNumber: number,
  status: number
) => {
  try {
    const url = apiUrls.guaranteeStatus;
    const body = {
      id_global,
      installment_number: installmentNumber,
      status,
    };
    return securePut(url, body);
  } catch (error) {
    console.error(
      'Error updating guarantee status:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const updateLoanRate = async (
  loanId: number,
  installmentNumber: number,
  percentagePoint: number
) => {
  try {
    const url = `/v1/private/guarantees/loans/${loanId}/rate`;
    const body = {
      installmentNumber,
      percentagePoint,
    };
    return securePut(url, body);
  } catch (error) {
    console.error('Error updating loan rate:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createPartialPayment = async (
  instrumentId: number,
  date: string,
  totalPayment: boolean,
  amount: number,
  installmentNumber: number
) => {
  try {
    const url = `/v1/private/guarantees/instruments/${instrumentId}/partial-payments`;
    const body = {
      date,
      totalPayment,
      amount,
      installment_number: installmentNumber,
    };
    return securePost(url, body);
  } catch (error) {
    console.error('Error creating partial payment:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getOngoingMonetization = async (
  cuit: string = '',
  page: number = 0,
  size: number = 10
) => {
  try {
    let url = `${apiUrls.ongoing}/ongoing?page=${page}&size=${size}`;

    if (cuit && cuit.trim() !== '') {
      let processedCuit = cuit.trim();

      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedCuit)) {
        processedCuit = processedCuit.replace(/-/g, '');
      }

      url += `&filterText=${encodeURIComponent(processedCuit)}`;
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching ongoing monetization:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeLoanDetail = async (id: string) => {
  try {
    const url = `${apiUrls.guaranteeGeneralLoans}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee loan detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeCheckDetail = async (id: string) => {
  try {
    const url = `${apiUrls.guaranteeGeneralChecks}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee check detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteePromissoryNoteDetail = async (id: string) => {
  try {
    const url = `${apiUrls.guaranteeGeneralPromissoryNotes}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee promissory note detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeCommercialEndorsementDetail = async (id: string) => {
  try {
    const url = `${apiUrls.guaranteeGeneralCommercialEndorsements}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee commercial endorsement detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getRateTypes = async () => {
  try {
    const url = '/v1/private/guarantees/rate-types/';
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching rate types:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateRateType = async (id: number, baseValue: number) => {
  try {
    const url = `/v1/private/guarantees/rate-types/${id}`;
    const body = {
      baseValue: baseValue,
    };
    return securePut(url, body);
  } catch (error) {
    console.error('Error updating rate type:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getLoanInstallments = async (id: string) => {
  try {
    const url = `/v1/private/guarantees/monetization/loans/installments/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching loan installments:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeWeighingData = async (
  type: 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements',
  id: string
) => {
  try {
    const url = `/v1/private/guarantees/weighing/${type}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching weighing data:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getCommissionTypes = async () => {
  try {
    const url = apiUrls.commissionTypes;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching commission types:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getCommissions = async (
  type: 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements',
  idGlobal: string
) => {
  try {
    const url = `${apiUrls.commissions}/${type}/${idGlobal}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching commissions:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createCommission = async (
  type: 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements',
  idGlobal: string,
  commissionData: {
    commission_type_id: number;
    name: string;
    amount: number;
  }
) => {
  try {
    const url = `${apiUrls.commissions}/${type}/${idGlobal}`;
    return securePost(url, commissionData);
  } catch (error) {
    console.error('Error creating commission:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getPurposeTypes = async () => {
  try {
    const url = apiUrls.purposeTypes;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching purpose types:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getRiskMetrics = async () => {
  try {
    const url = apiUrls.riskMetrics;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching risk metrics:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const updateRiskMetrics = async (fieldName: string, value: number) => {
  try {
    const url = apiUrls.riskMetrics;
    const body = {
      [fieldName]: value,
    };
    return securePut(url, body);
  } catch (error) {
    console.error(
      'Error updating risk metrics:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getRiskMetricsProtectors = async (
  page: number = 0,
  size: number = 20,
  date?: string,
  sortBy?: 'amount' | 'integrated',
  sortDirection?: 'asc' | 'desc',
  filterText?: string
) => {
  try {
    let url = `${apiUrls.riskMetricsProtectors}?page=${page}&size=${size}`;

    if (date) {
      url += `&date=${encodeURIComponent(date)}`;
    }

    if (sortBy) {
      url += `&sortBy=${encodeURIComponent(sortBy)}`;
    }

    if (sortDirection) {
      url += `&sortDirection=${encodeURIComponent(sortDirection)}`;
    }

    if (filterText && filterText.trim() !== '') {
      url += `&filterText=${encodeURIComponent(filterText.trim())}`;
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching risk metrics protectors:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getProtectorDetail = async (id: string | number) => {
  try {
    const url = `${apiUrls.protectorDetail}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createProtector = async (protectorData: {
  companyName: string;
  documentTypeId?: number | null;
  legalEntityTypeId?: number | null;
  cuit?: string | null;
  email?: string | null;
  meetingMinuteTypeId?: number | null;
  minuteNumber?: string | null;
  registrationDate?: string | null;
}) => {
  try {
    const url = '/v1/private/guarantees/protectors';
    return securePost(url, protectorData);
  } catch (error) {
    console.error('Error creating protector:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getProtectorFormMetadata = async () => {
  try {
    const url = '/v1/private/guarantees/form-metadata/protector';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getProtectorShareReceiptFormMetadata = async () => {
  try {
    const url = '/v1/private/guarantees/form-metadata/protector-share-receipt';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector share receipt form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createProtectorShareReceipt = async (shareReceiptData: {
  quantity: number;
  partner_id: number;
  protector_id: number;
  minute_date: string;
  minute_number: string;
}) => {
  try {
    const url = '/v1/private/guarantees/protector-share-receipts';
    return securePost(url, shareReceiptData);
  } catch (error) {
    console.error(
      'Error creating protector share receipt:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getCapitalContributionFormMetadata = async () => {
  try {
    const url = '/v1/private/guarantees/form-metadata/capital-contribution';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching capital contribution form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createCapitalContribution = async (contributionData: {
  protector_id: number;
  contribution_date: string;
  expiration_date: string;
  currency_id: number;
  amount: number;
  fixed_available_amount: number;
  participant_shareholders_return: boolean;
  is_tax_deductible: boolean;
  authorized_fund_id: number;
  previous_contribution_id: number | null;
  minute_date: string;
  minute_number: string;
}) => {
  try {
    const url = '/v1/private/guarantees/capital-contributions';
    return securePost(url, contributionData);
  } catch (error) {
    console.error(
      'Error creating capital contribution:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getContributionAssetIntegrationFormMetadata = async () => {
  try {
    const url =
      '/v1/private/guarantees/form-metadata/contribution-asset-integration';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching contribution asset integration form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createContributionAssetIntegration = async (integrationData: {
  contribution_id: number;
  asset_to_integrate_id: number;
  nominal_value: number;
  price: number;
  currency_id: number;
}) => {
  try {
    const url = '/v1/private/guarantees/contribution-asset-integrations';
    return securePost(url, integrationData);
  } catch (error) {
    console.error(
      'Error creating contribution asset integration:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getPartners = async () => {
  try {
    const url = '/v1/private/guarantees/partners';
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching partners:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getPartnerFormMetadata = async () => {
  try {
    const url = '/v1/private/guarantees/form-metadata/partner';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching partner form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createPartner = async (partnerData: {
  name: string;
  cuit: string;
  share_quantity: number;
  hierarchy_level_id: number;
}) => {
  try {
    const url = '/v1/private/guarantees/partners';
    return securePost(url, partnerData);
  } catch (error) {
    console.error('Error creating partner:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getFunds = async () => {
  try {
    const url = '/v1/private/guarantees/funds';
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching funds:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createFund = async (fundData: {
  name: string;
  is_tax_deductible: boolean;
}) => {
  try {
    const url = '/v1/private/guarantees/funds';
    return securePost(url, fundData);
  } catch (error) {
    console.error('Error creating fund:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getAssetsToIntegrate = async () => {
  try {
    const url = '/v1/private/guarantees/assets-to-integrate';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching assets to integrate:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createAssetToIntegrate = async (assetData: {
  name: string;
  description: string;
}) => {
  try {
    const url = '/v1/private/guarantees/assets-to-integrate';
    return securePost(url, assetData);
  } catch (error) {
    console.error(
      'Error creating asset to integrate:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getCapitalContributions = async (
  protectorId: string | number,
  page: number = 0,
  size: number = 10
) => {
  try {
    const url = `/v1/private/guarantees/capital-contributions/?protectorId=${protectorId}&page=${page}&size=${size}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching capital contributions:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getProtectorShareReceipts = async (
  protectorId: string | number,
  page: number = 0,
  size: number = 10
) => {
  try {
    const url = `/v1/private/guarantees/protector-share-receipts/?protectorId=${protectorId}&page=${page}&size=${size}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector share receipts:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getProtectorInstallmentConfrontations = async (
  protectorId: string | number,
  page: number = 0,
  size: number = 10
) => {
  try {
    const url = `/v1/private/guarantees/protector-installment-confrontations?protectorId=${protectorId}&page=${page}&size=${size}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector installment confrontations:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getSyndicatedOperations = async (
  type: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement',
  id: string
) => {
  try {
    // Mapear el tipo de garantía al formato del endpoint
    const typeMap: Record<string, string> = {
      loan: 'loans',
      check: 'checks',
      promissory_note: 'promissory-notes',
      commercial_endorsement: 'commercial-endorsements',
    };

    const endpointType = typeMap[type] || type;
    const url = `/v1/private/guarantees/syndicated/${endpointType}/${id}`;
    console.log('Fetching syndicated operations from URL:', url);
    const response = await secureGet(url);
    console.log('Syndicated operations response:', response);
    return response;
  } catch (error: any) {
    console.error(
      'Error fetching syndicated operations:',
      JSON.stringify(error, null, 2)
    );
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      url: error?.config?.url,
    });
    throw error;
  }
};

export const getReinsuranceInstitutions = async () => {
  try {
    const url = '/v1/private/guarantees/reinsurances/institutions';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching reinsurance institutions:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createReinsurance = async (reinsuranceData: {
  id_global: number;
  percentage: number;
  reinsurance_institution_id: number;
}) => {
  try {
    const url = '/v1/private/guarantees/reinsurances';
    return securePost(url, reinsuranceData);
  } catch (error) {
    console.error(
      'Error creating reinsurance:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const updateReinsurance = async (
  idGlobal: number,
  reinsuranceData: {
    percentage: number;
    reinsurance_institution_id: number;
  }
) => {
  try {
    const url = `/v1/private/guarantees/reinsurances/${idGlobal}`;
    return securePut(url, reinsuranceData);
  } catch (error) {
    console.error(
      'Error updating reinsurance:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};
